import React, { createContext, useContext, useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

// Pages
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import GuestDashboard from "./pages/GuestDashboard";
import Checklists from "./pages/Checklists";
import ChecklistDetail from "./pages/ChecklistDetail";
import Archive from "./pages/Archive";
import Settings from "./pages/Settings";
import Display from "./pages/Display";

// Components
import Nav from "./components/Nav";
import Toast from "./components/Toast";
import NotificationDrawer from "./components/NotificationDrawer";

// ============================================
// CONTEXTS
// ============================================
export const ThemeContext = createContext();
export const AuthContext = createContext();
export const ToastContext = createContext();
export const NotificationContext = createContext();
export const useTheme = () => useContext(ThemeContext);
export const useAuth = () => useContext(AuthContext);
export const useToast = () => useContext(ToastContext);
export const useNotifications = () => useContext(NotificationContext);

// ============================================
// PROVIDERS
// ============================================
function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => localStorage.getItem("theme") || "light");

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  const toggleTheme = () => setTheme(t => t === "light" ? "dark" : "light");

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isGuest, setIsGuest] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch("/.auth/me")
      .then(res => res.json())
      .then(data => {
        if (data.clientPrincipal) {
          setUser(data.clientPrincipal);
          setIsGuest(false);
        } else {
          setUser(null);
          if (!localStorage.getItem("guestId")) {
            localStorage.setItem("guestId", crypto.randomUUID());
          }
          setIsGuest(true);
        }
      })
      .catch(() => setIsGuest(true))
      .finally(() => setLoading(false));
  }, []);

  const logout = () => {
    window.location.href = "/.auth/logout";
  };

  const getInitials = (name) => {
    if (!name) return "U";
    const parts = name.trim().split(" ");
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  return (
    <AuthContext.Provider value={{ user, isGuest, loading, logout, getInitials }}>
      {children}
    </AuthContext.Provider>
  );
}

function ToastProvider({ children }) {
  const [toast, setToast] = useState(null);

  const showToast = (message, undoFn = null) => {
    setToast({ message, undoFn });
    setTimeout(() => setToast(null), 4000);
  };

  const dismissToast = () => setToast(null);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {toast && <Toast message={toast.message} onUndo={toast.undoFn} onDismiss={dismissToast} />}
    </ToastContext.Provider>
  );
}

function NotificationProvider({ children }) {
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const { user } = useContext(AuthContext);

  useEffect(() => {
    if (!user) return;
    fetchNotifications();
  }, [user]);

  const fetchNotifications = () => {
    setNotificationsLoading(true);
    fetch("/api/notifications")
      .then(res => res.json())
      .then(data => setNotifications(data || []))
      .catch(() => {})
      .finally(() => setNotificationsLoading(false));
  };

  const markAsRead = async (id) => {
    try {
      await fetch("/api/notifications", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, read: true })
      });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    } catch (err) {
      console.error("Failed to mark as read", err);
    }
  };

  const markAllAsRead = async () => {
    try {
      await fetch("/api/notifications", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ markAllRead: true })
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (err) {
      console.error("Failed to mark all as read", err);
    }
  };

  const dismissNotification = async (id) => {
    try {
      await fetch("/api/notifications", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id })
      });
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch (err) {
      console.error("Failed to dismiss notification", err);
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <NotificationContext.Provider value={{
      notifications,
      notificationsLoading,
      fetchNotifications,
      unreadCount,
      markAsRead,
      markAllAsRead,
      dismissNotification
    }}>
      {children}
    </NotificationContext.Provider>
  );
}

// ============================================
// ROUTE GUARD
// ============================================
function RequireAuth({ children }) {
  const { user, isGuest, loading } = useContext(AuthContext);
  if (loading) return null;
  if (isGuest) return <Navigate to="/guest" replace />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// ============================================
// LAYOUT (with Nav)
// ============================================
function Layout({ children }) {
  return (
    <>
      <Nav />
      {children}
    </>
  );
}

// ============================================
// APP
// ============================================
export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <ToastProvider>
            <NotificationProvider>
              <Routes>
                {/* Public */}
                <Route path="/login" element={<Login />} />
                <Route path="/guest" element={<Layout><GuestDashboard /></Layout>} />
                <Route path="/display" element={<Display />} />

                {/* Protected */}
                <Route path="/" element={<RequireAuth><Layout><Dashboard /></Layout></RequireAuth>} />
                <Route path="/checklists" element={<RequireAuth><Layout><Checklists /></Layout></RequireAuth>} />
                <Route path="/checklists/:id" element={<RequireAuth><Layout><ChecklistDetail /></Layout></RequireAuth>} />
                <Route path="/archive" element={<RequireAuth><Layout><Archive /></Layout></RequireAuth>} />
                <Route path="/settings" element={<RequireAuth><Layout><Settings /></Layout></RequireAuth>} />

                {/* Fallback */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </NotificationProvider>
          </ToastProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}